import React, { useState } from 'react';
import { Cloud, Settings, Clock, Activity } from 'lucide-react';
import CloudStorageSettings from '../components/storage/CloudStorageSettings';
import FileExplorer from '../components/storage/FileExplorer';
import RecentDocuments from '../components/storage/RecentDocuments';
import { useStorageStatus } from '../hooks/useStorage';
import { storageApi } from '../services/storageApi';
import type { StorageProvider, StorageActivityLog } from '../types/storage';

type Tab = 'settings' | 'google' | 'onedrive' | 'zoho' | 'recent' | 'activity';

export default function CloudStoragePage() {
  const [activeTab, setActiveTab] = useState<Tab>('settings');
  const { status } = useStorageStatus();
  const [activityLogs, setActivityLogs] = useState<StorageActivityLog[]>([]);
  const [activityLoaded, setActivityLoaded] = useState(false);

  const loadActivity = async () => {
    if (activityLoaded) return;
    const logs = await storageApi.getActivityLogs({ limit: 30 });
    setActivityLogs(logs);
    setActivityLoaded(true);
  };

  const tabs: { id: Tab; label: string; icon: React.ReactNode; disabled?: boolean }[] = [
    { id: 'settings', label: 'Settings', icon: <Settings className="w-4 h-4" /> },
    {
      id: 'google',
      label: 'Google Drive',
      icon: <GoogleDriveTabIcon />,
      disabled: !status?.google.connected,
    },
    {
      id: 'onedrive',
      label: 'OneDrive',
      icon: <OneDriveTabIcon />,
      disabled: !status?.onedrive.connected,
    },
    {
      id: 'zoho',
      label: 'WorkDrive',
      icon: <ZohoTabIcon />,
      disabled: !status?.zoho?.connected,
    },
    { id: 'recent', label: 'Recent', icon: <Clock className="w-4 h-4" /> },
    { id: 'activity', label: 'Activity', icon: <Activity className="w-4 h-4" /> },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 py-5">
            <div className="w-9 h-9 flex items-center justify-center bg-indigo-100 rounded-lg">
              <Cloud className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Cloud Storage</h1>
              <p className="text-xs text-gray-500">
                Google Drive, Microsoft OneDrive &amp; Zoho WorkDrive integration
              </p>
            </div>

            {/* Connection badges */}
            <div className="ml-auto flex items-center gap-2">
              {status?.google.connected && (
                <span className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-green-50 text-green-700 border border-green-200 rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                  Google Drive
                </span>
              )}
              {status?.onedrive.connected && (
                <span className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                  OneDrive
                </span>
              )}
              {status?.zoho?.connected && (
                <span className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-red-50 text-red-700 border border-red-200 rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                  WorkDrive
                </span>
              )}
            </div>
          </div>

          {/* Tabs */}
          <nav className="flex gap-0.5 -mb-px overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  if (tab.id === 'activity') loadActivity();
                }}
                disabled={tab.disabled}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-transparent disabled:hover:text-gray-500'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {activeTab === 'settings' && <CloudStorageSettings />}

        {activeTab === 'google' && status?.google.connected && (
          <div className="h-[calc(100vh-220px)]">
            <FileExplorer provider="google" />
          </div>
        )}

        {activeTab === 'onedrive' && status?.onedrive.connected && (
          <div className="h-[calc(100vh-220px)]">
            <FileExplorer provider="onedrive" />
          </div>
        )}

        {activeTab === 'zoho' && status?.zoho?.connected && (
          <div className="h-[calc(100vh-220px)]">
            <FileExplorer provider="zoho" />
          </div>
        )}

        {activeTab === 'recent' && (
          <div className="max-w-2xl bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Recent Documents</h2>
            <RecentDocuments />
          </div>
        )}

        {activeTab === 'activity' && (
          <div className="max-w-3xl bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">Activity Log</h2>
            </div>
            <div className="divide-y divide-gray-50">
              {activityLogs.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-10">No activity yet</p>
              ) : (
                activityLogs.map((log) => (
                  <ActivityRow key={log.id} log={log} />
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Activity row ─────────────────────────────────────────────────────────────

function ActivityRow({ log }: { log: StorageActivityLog }) {
  const actionLabel: Record<string, string> = {
    upload: 'Uploaded',
    download: 'Downloaded',
    create_folder: 'Created folder',
    delete: 'Deleted',
    sync: 'Synced',
    daily_backup: 'Daily backup',
    list_files: 'Browsed files',
  };

  return (
    <div className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50 text-sm">
      <ProviderBadge provider={log.provider} />
      <span className="text-gray-700 font-medium">{actionLabel[log.action] ?? log.action}</span>
      {log.metadata?.fileName != null && (
        <span className="text-gray-500 truncate flex-1">— {String(log.metadata.fileName)}</span>
      )}
      <span className="text-xs text-gray-400 flex-shrink-0 ml-auto">
        {new Date(log.created_at).toLocaleString('en-IN', {
          day: '2-digit', month: 'short', year: 'numeric',
          hour: '2-digit', minute: '2-digit',
        })}
      </span>
    </div>
  );
}

function ProviderBadge({ provider }: { provider: StorageProvider }) {
  const config: Record<StorageProvider, { label: string; className: string }> = {
    google: { label: 'Drive', className: 'bg-blue-50 text-blue-600' },
    onedrive: { label: 'OneDrive', className: 'bg-sky-50 text-sky-600' },
    zoho: { label: 'WorkDrive', className: 'bg-red-50 text-red-600' },
  };
  const { label, className } = config[provider] || { label: provider, className: 'bg-gray-50 text-gray-600' };
  return (
    <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${className}`}>
      {label}
    </span>
  );
}

function GoogleDriveTabIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none">
      <path d="M8.267 14.68l-5.9 2.22L6.54 22h10.92l2.4-3H8.267z" fill="#0066DA" />
      <path d="M8.267 14.68L12 8.22l3.733 6.46H8.267z" fill="#00AC47" />
      <path d="M12 8.22L8.267 2H15.733L19.46 8.22H12z" fill="#EA4335" />
      <path d="M19.46 8.22l-3.727 6.46 3.127 3.32L22.733 12 19.46 8.22z" fill="#FFBA00" />
    </svg>
  );
}

function OneDriveTabIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none">
      <path d="M9.9 10.5a5.5 5.5 0 0110.1 2.9A4 4 0 0120 21H6a4 4 0 01-1.4-7.7A5.5 5.5 0 019.9 10.5z" fill="#0078D4" />
    </svg>
  );
}

function ZohoTabIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16">
      <path d="M12 2L2 7l10 5 10-5-10-5z" fill="#E42527" />
      <path d="M2 17l10 5 10-5" fill="none" stroke="#E42527" strokeWidth="2" />
      <path d="M2 12l10 5 10-5" fill="none" stroke="#E42527" strokeWidth="2" />
    </svg>
  );
}
